import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Target } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { modules } from "@/lib/courseData";

const ModuleBody = ({ m }) => (
  <div className="grid lg:grid-cols-5 gap-8 pt-2 pb-4">
    <div className="lg:col-span-2 space-y-4">
      <p className="text-white/70 text-sm leading-relaxed">{m.summary}</p>
      <div className="flex gap-3 p-4 rounded-sm bg-fin-green/10 border border-fin-green/30">
        <Target className="h-5 w-5 text-fin-green shrink-0 mt-0.5" />
        <div className="text-sm text-white/90">
          <span className="text-fin-green font-semibold">Outcome: </span>
          {m.outcome}
        </div>
      </div>
    </div>
    <ul className="lg:col-span-3 space-y-3">
      {m.topics.map((t) => (
        <li key={t} className="flex items-start gap-3 text-white/80 text-sm">
          <CheckCircle2 className="h-4 w-4 text-fin-green shrink-0 mt-0.5" />
          {t}
        </li>
      ))}
    </ul>
  </div>
);

export const Curriculum = () => (
  <section id="curriculum" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 scroll-mt-20">
    <div className="mb-12 max-w-2xl">
      <div className="tracking-overline text-[10px] text-fin-green mb-3">Course curriculum</div>
      <h2 className="font-display text-3xl md:text-4xl text-white">5 modules. Zero to confident trader.</h2>
      <p className="text-white/60 mt-4">Tap any module to see exactly what you will learn.</p>
    </div>
    <div className="mb-10 rounded-lg overflow-hidden border border-white/10" data-testid="curriculum-banner">
      <img src="/img/curriculum-banner.jpg" alt="Module 01 — Basics of Stock Market preview" className="w-full h-auto" />
    </div>
    <Accordion type="single" collapsible defaultValue="m-0" className="space-y-4">
      {modules.map((m, i) => {
        const Icon = m.icon;
        return (
          <motion.div key={m.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}>
            <AccordionItem value={`m-${i}`} data-testid={`module-${i + 1}`} className="border border-white/10 rounded-md bg-[#141414] px-6 data-[state=open]:border-fin-green/50 transition-colors">
              <AccordionTrigger data-testid={`module-${i + 1}-toggle`} className="hover:no-underline py-6">
                <div className="flex items-center gap-5 text-left">
                  <div className="font-mono text-fin-green text-sm w-8">{String(i + 1).padStart(2, "0")}</div>
                  <div className="h-10 w-10 rounded-sm bg-fin-green/10 text-fin-green flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-white font-semibold text-base md:text-lg">{m.title}</div>
                    <div className="text-white/50 text-xs md:text-sm font-normal mt-1">{m.tagline}</div>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <ModuleBody m={m} />
              </AccordionContent>
            </AccordionItem>
          </motion.div>
        );
      })}
    </Accordion>
  </section>
);
